/**
 * Dashboard Layout
 * React Router
 */

import * as React from "react"
import { Link } from "react-router"
import { useAuth } from "@/hooks/use-auth"
import { Logo } from "@/components/ui/logo"
import { CreateProjectDialog } from "@/components/projects/create-project-dialog"
import { Search, Settings, ChevronDown, ChevronUp, LogOut, User, Bell, Sparkles } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface DashboardLayoutProps {
  children: React.ReactNode
}

/**
 * Dashboard Layout
 * Windmill-inspired brand shell with refined header and layered background depth.
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth()
  const [searchQuery, setSearchQuery] = React.useState("")
  const [dropdownOpen, setDropdownOpen] = React.useState(false)

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error("Logout failed:", error)
    }
  }

  const userInitials = user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "U"

  return (
    <div className="wm-shell-bg min-h-screen flex flex-col">
      <div className="pointer-events-none fixed inset-0 wm-grid-overlay opacity-40" />

      <header className="sticky top-0 z-40 border-b border-surface-border/80 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] w-full max-w-[1500px] items-center gap-3 px-3 md:px-6">
          <Link to="/dashboard" className="flex items-center gap-3 shrink-0">
            <Logo size="md" showText={false} />
            <div className="hidden sm:flex flex-col">
              <span className="font-display text-sm font-semibold text-navy">Audra Flow</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-[0.14em] text-gray-500">Product Studio</span>
                <span className="rounded-full border border-surface-border bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  {__APP_VERSION__}
                </span>
              </div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-2 rounded-full border border-brand-200/80 bg-brand-50/70 px-3 py-1 text-xs text-brand-700">
            <Sparkles className="h-3.5 w-3.5" />
            AI-powered workspace
          </div>

          <div className="relative ml-auto hidden max-w-[360px] flex-1 md:block">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <Search className="h-4 w-4 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full rounded-full border border-surface-border bg-white/90 pl-9 pr-4 text-sm text-gray-900 placeholder:text-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500/35 focus:border-brand-500"
            />
          </div>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <Link
              to="/admin/templates"
              className="inline-flex h-10 items-center rounded-full border border-surface-border bg-white/85 px-3 text-gray-700 transition-all hover:border-brand-300 hover:bg-white hover:text-navy md:px-4"
              title="Administration"
            >
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span className="hidden text-sm font-medium leading-none md:inline">Administration</span>
              </div>
            </Link>

            <CreateProjectDialog />

            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button className="h-10 rounded-full border border-surface-border bg-white/85 px-2.5 md:h-11 md:px-3.5 flex items-center gap-2.5 hover:border-brand-300 hover:bg-white transition-all">
                  <div className="w-7 h-7 rounded-full bg-[linear-gradient(145deg,#050019_0%,#512ab2_60%,#7c4af7_100%)] flex items-center justify-center shrink-0 shadow-soft-lg">
                    <span className="text-[11px] text-white font-semibold">{userInitials}</span>
                  </div>
                  <div className="hidden md:flex flex-col items-start">
                    <span className="text-sm text-gray-900 font-semibold leading-4">
                      {user?.name || "User"}
                    </span>
                    <span className="text-[11px] text-gray-500 leading-4 capitalize">
                      {user?.roles?.[0] || "Member"}
                    </span>
                  </div>
                  {dropdownOpen ? (
                    <ChevronUp className="hidden md:block w-4 h-4 text-gray-500 shrink-0" />
                  ) : (
                    <ChevronDown className="hidden md:block w-4 h-4 text-gray-500 shrink-0" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 rounded-2xl p-2 border-surface-border/80 bg-white/95 backdrop-blur-xl" align="end">
                <DropdownMenuLabel className="px-3 py-2">
                  <div className="flex flex-col space-y-1.5">
                    <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <span className="inline-flex w-fit px-2 py-0.5 text-xs font-medium text-brand-700 bg-brand-100 rounded-full capitalize">
                      {user?.roles?.[0] || "Member"}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="px-3 py-2.5 cursor-pointer">
                  <Link to="/settings" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    Profile Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="px-3 py-2.5 cursor-pointer">
                  <Link to="/settings" className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-gray-500" />
                    Notifications
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="px-3 py-2.5 cursor-pointer">
                  <Link to="/settings" className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-gray-500" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="px-3 py-2.5 text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1">
        <div className="mx-auto w-full max-w-[1500px] px-3 pb-10 pt-6 md:px-6 md:pt-8">
          {children}
        </div>
      </main>
    </div>
  )
}
